/* eslint-disable camelcase */
import { ISelectInputOption } from '@rocketmakers/armstrong';
import React = require('react');
import { useParams } from 'react-router-dom';
import {
  HeatingSystem,
  DeviceType,
  HeatingControlRelay,
  RuleActionType,
  RoomDialWrf06,
  RoomSensorWrf06,
  HeatingZoneID,
  RuleCategory,
  RuleVariant,
  DeviceInfo,
  PhysicalLocationID,
  RoomSensorWrf04,
  RoomSensorNovos3,
  RoomSensorRegin,
} from '../../../../shared/api-clients/_api';
import { useRoutePaths } from '../../../../shared/context/paths';
import { useBeeConfig } from '../../hooks/beeConfig';
import { routePaths } from '../../routes';
import { getDeviceWithZoneDescription, makeIdForDevice } from './utils';

type SensorDevice = RoomSensorWrf04 | RoomSensorWrf06 | RoomSensorNovos3 | RoomSensorRegin;

export function useConfigureBoilerHeatingZone(getZoneDescription) {
  const { heatingZone, boilerType } = useParams<{ heatingZone: HeatingZoneID; boilerType: string }>();

  const {
    createDevice,
    editDevice,
    removeDevice,
    devices,
    getDevicesByType,
    getDevicesByZone,
    getAssignedResourcesFor,
  } = useBeeConfig();

  const { replace } = useRoutePaths();

  const heatingSystem = HeatingSystem[boilerType?.toUpperCase()];

  const currentDevices = React.useMemo(() => {
    const hzDevices = heatingZone ? getDevicesByZone<SensorDevice>(heatingZone) : [];

    const relay = hzDevices.find(({ deviceType }) => deviceType === DeviceType.RELAY) as HeatingControlRelay;

    if (!relay) return;

    const tempDial = hzDevices.find(
      ({ deviceType, params: { action_type } }) =>
        deviceType === DeviceType.WRF06 && action_type === RuleActionType.CONTROL
    ) as RoomDialWrf06;

    const tempDialMonitoring = hzDevices.find(
      ({ deviceType, params: { action_type } }) =>
        deviceType === DeviceType.WRF06 && action_type === RuleActionType.MONITORING
    ) as RoomSensorWrf06;

    const tempSensor: SensorDevice = hzDevices.find(
      ({ deviceType, params: { action_type, primary_zone_temperature_sensor } }) =>
        ([DeviceType.WRF04, DeviceType.NOVOS3, DeviceType.REGIN].includes(deviceType) ||
          (deviceType === DeviceType.WRF06 && action_type === RuleActionType.MONITORING)) &&
        +primary_zone_temperature_sensor === 1
    );

    return {
      relay,
      tempDial,
      tempDialMonitoring,
      tempSensor,
    };
  }, [heatingZone, devices]);

  const {
    temperatureDialOptions,
    temperatureSensorOptions,
    assignedZones,
    assignedDials,
    assignedSensors,
    allWrf06TempSensors,
  } = React.useMemo(() => {
    const tempDials = getDevicesByType<RoomDialWrf06>([DeviceType.WRF06]).filter(
      ({ params: { action_type } }) => action_type === RuleActionType.CONTROL
    );

    const wrf06TempSensors = getDevicesByType<RoomSensorWrf06>([DeviceType.WRF06]).filter(
      ({ params: { action_type } }) => action_type === RuleActionType.MONITORING
    );

    const otherTempSensors = getDevicesByType<RoomSensorWrf04 | RoomSensorRegin | RoomSensorNovos3>([
      DeviceType.WRF04,
      DeviceType.REGIN,
      DeviceType.NOVOS3,
    ]);

    const tempSensors = [...otherTempSensors, ...wrf06TempSensors];

    return {
      temperatureDialOptions: tempDials.map<ISelectInputOption>(temperatureDial => ({
        id: makeIdForDevice(temperatureDial),
        name: getDeviceWithZoneDescription(temperatureDial, getZoneDescription),
        data: temperatureDial,
      })),
      temperatureSensorOptions: tempSensors.map<ISelectInputOption>(temperatureSensor => ({
        id: makeIdForDevice(temperatureSensor),
        name: getDeviceWithZoneDescription(temperatureSensor, getZoneDescription),
        data: temperatureSensor,
      })),
      assignedZones: getAssignedResourcesFor(
        [DeviceType.RELAY, DeviceType.WRF06, DeviceType.WRF04, DeviceType.REGIN, DeviceType.NOVOS3],
        'zone_id',
        heatingZone
      ),
      assignedDials: tempDials
        .filter(({ params }) => params.zone_id && currentDevices?.tempDial?.params.zone_id !== params.zone_id)
        .map(makeIdForDevice),
      assignedSensors: tempSensors
        .filter(({ params }) => params.zone_id && currentDevices?.tempSensor?.params.zone_id !== params.zone_id)
        .map(makeIdForDevice),
      allWrf06TempSensors: wrf06TempSensors,
    };
  }, [devices]);

  const assignToZone = <T extends DeviceInfo>(
    device: T,
    zone_id: HeatingZoneID,
    isPrimaryTemperatureSensor: 0 | 1 = 0
  ) => {
    const assignedDevice = {
      ...device,
      params: {
        ...device.params,
        zone_id,
      },
    };

    if (device.params.action_type === RuleActionType.MONITORING) {
      editDevice(device, {
        ...assignedDevice,
        params: {
          ...assignedDevice.params,
          primary_zone_temperature_sensor: isPrimaryTemperatureSensor,
        },
      });
    } else {
      editDevice(device, assignedDevice);
    }
  };

  const removeFromZone = <T extends DeviceInfo>(device: T) => {
    if (device.params.action_type === RuleActionType.MONITORING) {
      editDevice(device, {
        ...device,
        params: { ...device.params, zone_id: null, primary_zone_temperature_sensor: 0 },
      });
    } else {
      editDevice(device, {
        ...device,
        params: { ...device.params, zone_id: null },
      });
    }
  };

  const findWrf06TempSensorInLocation = (tempDialLocation: PhysicalLocationID, isPrimarySensor = false) =>
    isPrimarySensor
      ? allWrf06TempSensors.find(
          ({ params: { install_location, primary_zone_temperature_sensor } }) =>
            install_location === tempDialLocation && +primary_zone_temperature_sensor === 1
        )
      : allWrf06TempSensors.find(({ params: { install_location } }) => install_location === tempDialLocation);

  const handleCreate = (
    zone_id: HeatingZoneID,
    relayAddress: number,
    tempDial: RoomDialWrf06,
    tempSensor: SensorDevice
  ) => {
    createDevice({
      deviceType: DeviceType.RELAY,
      params: {
        address: relayAddress,
        zone_id,
        category: RuleCategory.HEATING,
        heating_system: heatingSystem,
        action_type: RuleActionType.CONTROL,
        variant: RuleVariant.SYSTEM_BOILER,
      },
    });

    assignToZone(tempDial, zone_id);
    assignToZone(tempSensor, zone_id, 1);

    if (tempSensor.deviceType !== DeviceType.WRF06) {
      const tempDialMonitoring = findWrf06TempSensorInLocation(tempDial.params.install_location);

      if (!tempDialMonitoring.params.zone_id) assignToZone(tempDialMonitoring, zone_id);
    }
  };

  const handleDelete = React.useCallback(() => {
    const { tempDial, tempDialMonitoring, tempSensor, relay } = currentDevices;
    const isCurrentDialAssignedAsSensor = !!findWrf06TempSensorInLocation(tempDial.params.install_location, true);

    removeFromZone(tempDial);
    removeFromZone(tempSensor);
    removeDevice(relay);

    if (!isCurrentDialAssignedAsSensor) removeFromZone(tempDialMonitoring);

    replace(routePaths.heatingSystem.assignBoilerHeatingZones.root, { boilerType });
  }, [currentDevices]);

  const handleEdit = React.useCallback(
    (zone_id: HeatingZoneID, relayAddress: number, tempDial: RoomDialWrf06, tempSensor: SensorDevice) => {
      const hasZoneChanged = heatingZone !== zone_id;
      const hasDialChanged = currentDevices ? currentDevices.tempDial !== tempDial : false;
      const hasSensorChanged = currentDevices ? currentDevices.tempSensor !== tempSensor : false;
      const hasRelayChanged = currentDevices ? currentDevices.relay.params.address !== relayAddress : false;

      const currentlyConfiguredHeatingSystem = getAssignedResourcesFor(
        [DeviceType.RELAY, DeviceType.MASTERTHERM],
        'heating_system',
        null
      )[0];

      const heatingZoneRelays = getDevicesByType<HeatingControlRelay>([DeviceType.RELAY]).filter(
        ({ params }) => params.zone_id && params.category === RuleCategory.HEATING
      );

      const otherHeatingZoneRelays = heatingZoneRelays.filter(relay => relay !== currentDevices.relay);

      const newlySelectedHeatingSystem = heatingSystem;

      const hasHeatingSystemChanged = newlySelectedHeatingSystem !== currentlyConfiguredHeatingSystem;

      const changeDial = <T extends RoomDialWrf06>(currentDial: T, selectedDial: T) => {
        const currentDialMonitoring = findWrf06TempSensorInLocation(currentDial.params.install_location);
        const selectedDialMonitoring = findWrf06TempSensorInLocation(selectedDial.params.install_location);

        const isCurrentDialAssignedAsSensor = !!findWrf06TempSensorInLocation(
          currentDial.params.install_location,
          true
        );

        if (currentDial !== selectedDial) {
          removeFromZone(currentDial);
          if (!isCurrentDialAssignedAsSensor) {
            removeFromZone(currentDialMonitoring);
          }
        }

        assignToZone(selectedDial, zone_id);
        assignToZone(selectedDialMonitoring, zone_id);
      };

      const changeSensor = <T extends SensorDevice>(currentSensor: T, selectedSensor: T) => {
        if (currentSensor !== selectedSensor) removeFromZone(currentSensor);
        assignToZone(selectedSensor, zone_id, 1);
      };

      if (hasDialChanged || hasZoneChanged) {
        changeDial(currentDevices.tempDial, tempDial);
      }

      if (hasSensorChanged || hasZoneChanged) {
        changeSensor(currentDevices.tempSensor, tempSensor);
      }

      if (hasRelayChanged || hasZoneChanged) {
        editDevice(currentDevices.relay, {
          deviceType: DeviceType.RELAY,
          params: {
            address: relayAddress,
            zone_id,
            category: RuleCategory.HEATING,
            action_type: RuleActionType.CONTROL,
            heating_system: heatingSystem,
            variant: RuleVariant.SYSTEM_BOILER,
          },
        });

        if (hasHeatingSystemChanged) {
          otherHeatingZoneRelays.map(relay =>
            editDevice(relay, {
              ...relay,
              params: { ...relay.params, heating_system: heatingSystem },
            })
          );
        }
      }

      if (!hasRelayChanged && hasHeatingSystemChanged) {
        heatingZoneRelays.map(relay =>
          editDevice(relay, {
            ...relay,
            params: { ...relay.params, heating_system: heatingSystem },
          })
        );
      }
    },
    [currentDevices]
  );

  const handleSave = dataBinder => {
    const zone_id = dataBinder.getValue('heatingZoneID');
    const { data: selectedTempDial } = temperatureDialOptions.find(
      ({ id }) => id === dataBinder.getValue('temperatureDial')
    );

    const { data: selectedTempSensor } = temperatureSensorOptions.find(
      ({ id }) => id === dataBinder.getValue('temperatureSensor')
    );

    const selectedRelayAddress = dataBinder.getValue('control');

    if (heatingZone) {
      handleEdit(zone_id, selectedRelayAddress, selectedTempDial, selectedTempSensor);
    } else {
      handleCreate(zone_id, selectedRelayAddress, selectedTempDial, selectedTempSensor);
    }

    replace(routePaths.heatingSystem.assignBoilerHeatingZones.root, { boilerType });
  };

  return {
    handleCreate,
    handleDelete,
    handleEdit,
    handleSave,
    currentDevices,
    temperatureDialOptions,
    temperatureSensorOptions,
    assignedZones,
    assignedDials,
    assignedSensors,
    allWrf06TempSensors,
  };
}
